import enrichmentIcon from '@disy/cadenza-icons/enrich.svg';

import { Modal } from 'ui/dialog/modal/modal';
import { ProgressBar } from 'ui/progress-bar/progress-bar';

import { createWorkbookUrlWithHashAsQueryParam } from 'cadenza/api-client/workbook-api';
import { getStore } from 'cadenza/store/cadenza-store';
import { cadenzaUrl } from 'cadenza/utils/cadenza-url/cadenza-url';
import { navigateWithinWorkbook } from 'cadenza/utils/confirm-navigation-util';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import { getWorkbookId, getWorkbookNameAndHash } from 'cadenza/workbook/workbook.state';
import { errorAlert } from 'cadenza/alert';
import type { WorkOrderId } from 'cadenza/api-client/workorder-api';
import { deleteWorkOrders } from 'cadenza/api-client/workorder-api';
import type { AttributeName } from 'cadenza/model/attribute-name';
import { type ObjecttypeIdString, objecttypeIdToString } from 'cadenza/repository/model/objecttype-id';
import type { ObjecttypeName } from 'cadenza/repository/model/objecttype-name';
import type { RepositoryName } from 'cadenza/repository/model/repository-name';
import { on } from 'cadenza/utils/event-util';
import { formatPercent } from 'cadenza/utils/format/format';
import { type Icon, icon } from 'cadenza/utils/icon/icon';
import type { EnrichmentType } from 'cadenza/workbook/api/enrichment-type';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { EnrichmentParametrization } from 'cadenza/workbook/enrichment/model/enrichment-parametrization-dto';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id';
import { createAttributeId } from 'cadenza/workbook/model/attribute-id-util';
import { observeForEnrichmentResult } from 'cadenza/workbook/workbook-panel/databrowser/create-enrichment-context-menu-utilities';
import { IsochroneSmoothing } from 'cadenza/map/routing/model/isochrone-smoothing';

import i18n from './enrichment-utils.properties';

/**
 * Resolves all keys K of the given type T where T[K] = will resolve to the type V.
 *
 * Sadly, indexing T with T[KeysWithType<T, ...>>] sometimes doesn't resolve to usable results,
 * especially when working with generic input parameters. In the Enrichment Dialog, this type is only
 * for providing autocomplete and type safety for string parameters which serve as keys for T.
 *
 * @see https://stackoverflow.com/q/54520676
 */
export type KeysWithType<T, V> = { [K in keyof T]-?: T[K] extends V ? K : never }[keyof T];

type ObjecttypeNameKeys<P> = KeysWithType<P, ObjecttypeName | undefined>;
type AttributeNameKeys<P> = KeysWithType<P, AttributeName | undefined>;

export function createAttributeIdFromParams<T extends EnrichmentType, P = EnrichmentParametrization<T>>(
  objecttypeNameKey: ObjecttypeNameKeys<P>,
  attributeNameKey: AttributeNameKeys<P>,
  params: P,
  enrichment: { repositoryName: RepositoryName; enrichedObjecttypeId: ObjecttypeIdString },
): AttributeId | undefined {
  const objecttypeName = params[objecttypeNameKey];
  const objecttypeId =
    objecttypeName === undefined ?
      enrichment.enrichedObjecttypeId
    : objecttypeIdToString(enrichment.repositoryName, objecttypeName as unknown as ObjecttypeName);
  const attributeName = params[attributeNameKey];
  if (attributeName) {
    return createAttributeId(objecttypeId, attributeName as unknown as AttributeName);
  }
}

export function getAttributeIdFromWorkbookAttributeDto(attribute?: WorkbookAttributeDto) {
  // if there's a 'baseId', this will be the AttributeId for this active attribute, otherwise 'id' is of type AttributeId
  return attribute?.baseId ?? (attribute?.id as AttributeId | undefined);
}

export function createEnrichmentLoadingDialog(
  progressBar: ProgressBar,
  {
    titleIcon,
    titleText,
  }: {
    titleIcon?: Icon;
    titleText?: string;
  } = {},
) {
  return new Modal({
    title: titleText ?? i18n('progressDialog.title'),
    titleIcon: icon(titleIcon ?? enrichmentIcon),
    content: () => Promise.resolve(progressBar),
    cancelButton: true,
  });
}

export function createEnrichmentDialogProgressBar() {
  const progressBar = new ProgressBar();
  progressBar.valueTextFormatter = () => formatPercent(progressBar.value, { maximumFractionDigits: 0 });
  progressBar.value = 0;
  return progressBar;
}

export function createEnrichmentErrorAlert() {
  return errorAlert(i18n('progressDialog.errorAlert.title'), i18n('progressDialog.errorAlert.message'));
}

export async function createEnrichmentProgressDialog(workOrderId: WorkOrderId) {
  const progressBar = createEnrichmentDialogProgressBar();
  const loadingDialog = createEnrichmentLoadingDialog(progressBar);
  loadingDialog.connectAndShow();
  on(loadingDialog, 'cancel', () => deleteWorkOrders([workOrderId]));
  try {
    return await observeForEnrichmentResult(workOrderId).progress(({ step, progress }) => {
      progressBar.value = progress;
      progressBar.label = mapStepToLang(step);
    });
  } finally {
    loadingDialog.close();
  }
}

export function userInputSmoothingToKey(value: number) {
  return IsochroneSmoothing.values[value];
}

export function smoothingValueToUserInputValue(value: IsochroneSmoothing) {
  return IsochroneSmoothing.values.findIndex((smoothingValue) => smoothingValue === value);
}

/** Make sure this is executed at the end of an enrichment workflow, so that all objecttypes are
 * added and all commands executed first. This is important as this function will immediately
 * terminate the regular promise and execution flow and navigate away to the new worksheet! */
export function openEnrichmentReportWorksheet(createdReportWorksheetId?: WorksheetId) {
  const worksheetId = createdReportWorksheetId;
  if (worksheetId) {
    const repositoryName = getWorkbookId(getStore().getState()).repositoryName;
    const workbookNameAndHash = getWorkbookNameAndHash(getStore().getState());
    const url = createWorkbookUrlWithHashAsQueryParam(repositoryName, workbookNameAndHash, {
      worksheetId,
    });
    navigateWithinWorkbook(cadenzaUrl(url));
  }
}

const SUPPORTED_PROGRESS_STEPS = new Set([
  'self-service-step-write-meta',
  'self-service-step-create-objecttype',
  'self-service-step-create-workbook-relation',
  'self-service-step-create-table',
  'self-service-step-import-data',
  'self-service-step-create-index',
  'spatial-measure-query',
]);

export function mapStepToLang(step: string) {
  if (SUPPORTED_PROGRESS_STEPS.has(step)) {
    return i18n(`progress.step.{{${step}}}`);
  }
  return '';
}

export function getGeometryAttributes(attributes: WorkbookAttributeDto[]): WorkbookAttributeDto[] {
  const result = [];
  for (const attribute of attributes) {
    if (attribute.category === 'DIMENSION' && (attribute.type === 'GEOMETRY' || attribute.hasGeometryRelation)) {
      result.push(attribute);
    }
  }
  return result;
}
